import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactElement,
  ReactNode,
} from "react";
import { cn } from "@/lib/utils";

export type ButtonType = "primary" | "secondary" | "tertiary" | "ghost";
export type ButtonShape = "rounded" | "sharp";
export type IconSize = "ex-small" | "small" | "medium";

type Common = {
  type?: ButtonType;
  shape?: ButtonShape;
  showTrailingIcon?: boolean;
  trailingIcon?: ReactNode;
  trailingIconSize?: IconSize;
  text: string;
};

type IconDiscrim =
  | { iconOnly: true; "aria-label": string }
  | { iconOnly?: false };

type AsButton = Common
  & IconDiscrim
  & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type">
  & {
    href?: undefined;
    htmlType?: "button" | "submit" | "reset";
  };

type AsAnchor = Common
  & IconDiscrim
  & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "type">
  & {
    href: string;
    disabled?: undefined;
  };

export type ButtonProps = AsButton | AsAnchor;

function buildClasses(
  type: ButtonType,
  shape: ButtonShape,
  iconOnly: boolean,
  trailingIconSize: IconSize,
  className: string | undefined,
): string {
  return cn(
    "custom-button",
    `custom-btn-${type}`,
    shape === "rounded" ? "btn-rounded" : "btn-sharp",
    iconOnly && `trailing-icon-only-${trailingIconSize}`,
    className,
  );
}

function buildInner(
  iconOnly: boolean,
  text: string,
  showTrailingIcon: boolean,
  trailingIcon: ReactNode | undefined,
  trailingIconSize: IconSize,
): ReactElement {
  return (
    <>
      {!iconOnly && <span>{text}</span>}
      {showTrailingIcon && (
        <span className={cn("btn-trailing-icon", `icon-size-${trailingIconSize}`)}>
          {trailingIcon}
        </span>
      )}
    </>
  );
}

export function Button(props: ButtonProps): ReactElement {
  if (props.href !== undefined) {
    const {
      type = "primary",
      shape = "rounded",
      iconOnly = false,
      showTrailingIcon = false,
      trailingIcon,
      trailingIconSize = "ex-small",
      text,
      href,
      className,
      ...anchorRest
    } = props;
    return (
      <a
        href={href}
        className={buildClasses(type, shape, iconOnly, trailingIconSize, className)}
        {...anchorRest}
      >
        {buildInner(iconOnly, text, showTrailingIcon, trailingIcon, trailingIconSize)}
      </a>
    );
  }

  const {
    type = "primary",
    shape = "rounded",
    iconOnly = false,
    showTrailingIcon = false,
    trailingIcon,
    trailingIconSize = "ex-small",
    text,
    htmlType = "button",
    disabled,
    className,
    ...buttonRest
  } = props;
  return (
    <button
      type={htmlType}
      disabled={disabled}
      className={cn(
        buildClasses(type, shape, iconOnly, trailingIconSize, className),
        disabled && "is-disabled",
      )}
      {...buttonRest}
    >
      {buildInner(iconOnly, text, showTrailingIcon, trailingIcon, trailingIconSize)}
    </button>
  );
}
